/**
 * OPS HTTP 验签。原文：`{timestamp}\n{METHOD}\n{path}\n{nonce或空}`。
 * path 为 `/user/...`，不含 `/api/v1`。未登录：空 nonce，不贴 X-Nonce。
 */

import { signOps } from './opsDeviceKey';

export const OPS_SIGN_ALGO = 'ES256' as const;
export const OPS_KEY_PURPOSE = 'OPS' as const;

export type OpsSignMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type OpsSignedRequest = {
  timestamp: number;
  nonce: string;
  headers: Record<string, string>;
};

function normalizeSignPath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) throw new Error('opsDeviceSign: path 不能为空');
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const noQuery = withSlash.split('?')[0] ?? withSlash;
  return noQuery.length > 1 && noQuery.endsWith('/') ? noQuery.slice(0, -1) : noQuery;
}

export async function signOpsHttp(options: {
  method: OpsSignMethod;
  path: string;
  timestamp: number;
  nonce?: string;
  userId?: string;
}): Promise<OpsSignedRequest> {
  const timestamp = options.timestamp;
  if (!Number.isFinite(timestamp)) throw new Error('opsDeviceSign: timestamp 无效');
  const method = options.method.toUpperCase() as OpsSignMethod;
  const path = normalizeSignPath(options.path);
  const nonceValue = options.nonce?.trim() ?? '';
  const signature = await signOps(`${timestamp}\n${method}\n${path}\n${nonceValue}`);

  const headers: Record<string, string> = {
    'X-Timestamp': String(timestamp),
    'X-Sign-Algo': OPS_SIGN_ALGO,
    'X-Key-Purpose': OPS_KEY_PURPOSE,
    'X-Signature': signature,
  };
  if (nonceValue) headers['X-Nonce'] = nonceValue;
  const userId = options.userId?.trim();
  if (userId) headers.userId = userId;

  return { timestamp, nonce: nonceValue, headers };
}

/** 未登录：空 nonce */
export async function beginOpsSignedRequestAnonymous(options: {
  method: OpsSignMethod;
  path: string;
}): Promise<OpsSignedRequest> {
  return signOpsHttp({
    method: options.method,
    path: options.path,
    timestamp: Date.now(),
    nonce: '',
  });
}
